import { motion } from "framer-motion";
import { Store } from "lucide-react";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import type { MerchantOfOfferVO } from "../types";
import { fadeUp } from "../utils";

type StorefrontMerchantsSectionProps = {
  merchants: MerchantOfOfferVO[];
  totalMerchantCount: number;
  loading: boolean;
  pageNumber: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onMerchantSelect: (merchantId: number) => void;
};

export function StorefrontMerchantsSection({
  merchants,
  totalMerchantCount,
  loading,
  pageNumber,
  totalPages,
  onPageChange,
  onMerchantSelect,
}: StorefrontMerchantsSectionProps) {
  return (
    <section
      id="merchants"
      className="bg-white px-4 py-16 sm:px-6 lg:px-8"
      aria-label="Partner merchants"
    >
      <motion.div
        className="mx-auto max-w-screen-2xl"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={fadeUp}
      >
        <div className="text-center">
          <span className="inline-block bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-violet-700">
            Merchants
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {loading
              ? "Loading partner merchants…"
              : "Brands You Trust. Rewards You Deserve."}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-slate-600">
            {loading
              ? "Discover exclusive offers from trusted brands across our merchant network."
              : `Explore ${totalMerchantCount.toLocaleString()}+ partner merchants and unlock exclusive offers from brands you love.`}
          </p>
        </div>

        {loading ? (
          <p className="mt-12 py-16 text-center text-sm text-slate-600">
            Loading merchants…
          </p>
        ) : merchants.length === 0 ? (
          <p className="mt-12 py-16 text-center text-sm text-slate-600">
            No merchants available at the moment.
          </p>
        ) : (
          <>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {merchants.map((merchant) => (
                <button
                  key={merchant.id}
                  type="button"
                  onClick={() => onMerchantSelect(merchant.id)}
                  className="group flex cursor-pointer flex-col overflow-hidden border border-slate-200 bg-white text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  aria-label={`View offers from ${merchant.merchantName}`}
                >
                  <div className="flex h-36 items-center justify-center border-b border-slate-100 bg-white p-4">
                    {merchant.merchantImageUri ? (
                      <img
                        src={merchant.merchantImageUri}
                        alt={merchant.merchantName}
                        className="max-h-24 max-w-full object-contain"
                      />
                    ) : (
                      <Store
                        className="h-12 w-12 text-violet-400"
                        aria-hidden
                      />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <h3 className="font-semibold text-slate-900">
                      {merchant.merchantName}
                    </h3>
                  </div>
                </button>
              ))}
            </div>

            <EnhancedPaginationControl
              totalPages={totalPages}
              currentPage={pageNumber}
              onPageChange={onPageChange}
              className="mt-8"
            />
          </>
        )}
      </motion.div>
    </section>
  );
}
